/**
 * Поле «Культура» у семенной позиции бюджета — выпадашка вместо свободного
 * текста (AK-704 п.2, продолжение AK-656).
 *
 * Почему это отдельный компонент, а не ещё одна ветка `ItemKindSelect`: у
 * остальных разделов «тип» — просто слово («гербицид»), а у семян это
 * КУЛЬТУРА — сущность, у которой есть системный id. Он нужен не для красоты:
 * по нему сужается соседний выбор сорта («Системный справочник»), иначе
 * агроном ищет сорт пшеницы среди сортов всех культур сразу.
 *
 * Три правила, общие со всеми выпадашками проекта:
 *
 * 1. **Алфавит + поиск, без «топ-N»** — сквозное правило. Ранжирование то же
 *    (`searchRank`): точное совпадение и начало слова выше буквы в середине.
 * 2. **Creatable.** «Использовать своё: …» остаётся: бюджет верстают раньше,
 *    чем номенклатура хозяйства закрыта, и незнакомая культура не должна
 *    блокировать строку. Поэтому `item_kind` в БД остаётся ТЕКСТОМ и миграции
 *    не нужно — id культуры живёт только в форме, ради фильтра сортов.
 * 3. **Источник — номенклатура хозяйства** (`/reference/tenant-crops/picker`),
 *    а не весь системный справочник: в системном сотни культур, которых у
 *    хозяйства нет, и список превращается в свалку (тот же выбор сделан в
 *    форме посева и в AddCropDialog).
 *
 * «Пусто ≠ мусор»: пока номенклатура пуста, поле не притворяется сломанным —
 * оно прямо говорит, что справочник пуст, и пускает ввести своё.
 */
import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SEARCH_RANK_NONE, searchRank } from '@/lib/reference/normalizeSearch';
import { useCropPicker, type CropPickerItem } from '@/components/fields/card/api';
import { cn } from '@/lib/utils';

export interface CropOption {
  /** Системная культура (`reference.crops`); null — культура вне номенклатуры. */
  cropId: string | null;
  name: string;
}

/** Нормализованный ключ: регистр и «ё» — не разница («Гречиха» = «гречиха»). */
function cropKey(name: string): string {
  return name.trim().toLowerCase().replace(/ё/g, 'е');
}

/**
 * Культуры номенклатуры + уже сохранённое значение позиции, по алфавиту.
 *
 * Сохранённое значение добавляем ОБЯЗАТЕЛЬНО: позицию могли завести до того,
 * как культуру убрали из номенклатуры, — и открытие диалога не должно молча
 * стирать то, что человек уже вписал.
 */
export function cropOptions(items: readonly CropPickerItem[], current: string): CropOption[] {
  const seen = new Set<string>();
  const out: CropOption[] = [];
  for (const item of items) {
    const name = (item.name || item.canonical_name || '').trim();
    if (!name || seen.has(cropKey(name))) continue;
    seen.add(cropKey(name));
    out.push({ cropId: item.crop_id, name });
  }
  const saved = current.trim();
  if (saved && !seen.has(cropKey(saved))) out.push({ cropId: null, name: saved });
  return out.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

/**
 * Системный id культуры по её НАЗВАНИЮ (то, что лежит в `item_kind`).
 *
 * Нужен при открытии уже сохранённой позиции: в БД едет текст, а фильтр сортов
 * работает по id. Без этого разрешения человеку пришлось бы заново выбирать ту
 * же самую культуру, только чтобы разблокировать поле сорта.
 *
 * `null` — культуры нет в номенклатуре (вписана «своя»): сузить сорта нечем, и
 * притворяться, что мы её узнали, нельзя.
 */
export function resolveCropId(items: readonly CropPickerItem[], name: string): string | null {
  const wanted = cropKey(name);
  if (!wanted) return null;
  for (const item of items) {
    const itemName = (item.name || item.canonical_name || '').trim();
    if (itemName && cropKey(itemName) === wanted) return item.crop_id;
  }
  return null;
}

/** Ранжирование под запрос: релевантность, внутри группы — алфавит. */
export function rankCrops(options: readonly CropOption[], term: string): CropOption[] {
  return options
    .map((option) => ({ option, rank: searchRank(term, option.name) }))
    .filter((row) => row.rank !== SEARCH_RANK_NONE)
    .sort((a, b) => a.rank - b.rank || a.option.name.localeCompare(b.option.name, 'ru'))
    .map((row) => row.option);
}

export function CropKindSelect({
  value,
  onChange,
  label,
}: {
  /** Название культуры (то, что ляжет в `item_kind`). */
  value: string;
  /** Отдаём и название, и id системной культуры — id нужен фильтру сортов. */
  onChange: (name: string, cropId: string | null) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const crops = useCropPicker();

  const all = useMemo(() => cropOptions(crops.data ?? [], value), [crops.data, value]);
  const options = useMemo(() => rankCrops(all, term), [all, term]);

  const custom = term.trim();
  const canCreate = custom.length > 0 && !all.some((o) => cropKey(o.name) === cropKey(custom));

  const pick = (name: string, cropId: string | null) => {
    // Повторный клик по выбранному — снятие: культура не обязательна.
    if (cropKey(name) === cropKey(value)) onChange('', null);
    else onChange(name, cropId);
    setTerm('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between font-normal"
          aria-label={label}
        >
          <span className={cn('truncate', !value && 'text-muted-foreground')}>
            {value || `${label}…`}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="relative border-b border-border p-2">
          <Search className="absolute left-4 top-4 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Поиск или своя культура"
            className="h-8 pl-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {options.length === 0 && !canCreate && (
            <p className="p-2 text-sm text-muted-foreground">
              {all.length === 0
                ? 'Культуры хозяйства ещё не заведены — впишите свою.'
                : 'Ничего не нашлось'}
            </p>
          )}
          {options.map((option) => (
            <button
              key={`${option.cropId ?? 'own'}:${option.name}`}
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-accent"
              onClick={() => pick(option.name, option.cropId)}
            >
              <Check
                className={cn(
                  'h-4 w-4 shrink-0',
                  cropKey(option.name) === cropKey(value) ? 'opacity-100' : 'opacity-0',
                )}
              />
              <span className="truncate">{option.name}</span>
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-accent"
              onClick={() => pick(custom, null)}
            >
              <Plus className="h-4 w-4 shrink-0" />
              Использовать своё: «{custom}»
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
